import styles from './NotificationsSettingsForm.module.css'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { SaveButton } from './SaveButton'
import { TestNotificationsButton } from './TestNotificationsButton'
import { Input } from './Input'
import { Checkbox } from './Checkbox'
import { useEffect, useState } from 'react'
import { useSendTestNotificationMutation } from 'services/pushNotificationSubscription'
import { PushNotificationInput } from './PushNotificationInput'
import { Link } from 'react-router-dom'

interface NotificationsSettingsFormProps {
    value: any
    onSubmit: (value: any, event: any) => void
    onFollowLink?: (event: any) => void 
    isLoading?: boolean
}

const formSchema = z.object({
    emailForNotifications: z
    .union( [
        z.literal( '' ),
        z.string().email()
        .optional()
        .nullable()
    ]),
    emailNotificationsEnabled: z
        .boolean()
        .optional()
        .nullable(),
    phone: z
        .string()
        .optional()
        .nullable(),
    whatsappNotificationsEnabled: z
        .boolean()
        .optional()
        .nullable(),
    pushNotificationsEnabled: z
        .boolean()
        .optional()
        .nullable()
}).passthrough()

type FormData = z.infer<typeof formSchema>

export const NotificationsSettingsForm = (props: NotificationsSettingsFormProps): JSX.Element => {
    const {
        value,
        onSubmit,
        onFollowLink
    } = props

    const [subscribed, setSubscribed] = useState<boolean>(false)

    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        formState: { errors, isDirty, isValid },
    } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: value,
    })

    const handleFormSubmit = (value: any, event: any) => {
        onSubmit?.(value, event)
        console.log(`handleFormSubmit`)
    }  

    const handleSaveButtonClick = (event: any) => {
        console.log(`handleSaveButtonClick`)
    }

    const handleSubscribedChanged = (event: any) => {
        setSubscribed(event)
    }

    const handleFollowLinkToInstruction = (event: any) => {
        onFollowLink?.(``)
    }

    return (
        <form 
            className={styles.Form}
            onSubmit={handleSubmit(handleFormSubmit)}>
            <div className={styles.Form__StartContainer}>
                <div className={styles.Form__Heading1}>To see changes your competitors are making please
                    enable Notifications
                </div>
            </div>
            <div className={styles.Form__CenterContainer}>
                <div className={styles.Form__FieldsContainer}>
                    <div className={styles.Form__EmailNotificationsEnabledContainer}>
                        <Controller
                                name="emailNotificationsEnabled"
                                control={control}
                                render={({
                                    field: { onChange, onBlur, value, name, ref },
                                    fieldState: { invalid, isTouched, isDirty, error },
                                    formState: { dirtyFields },
                                }) => (
                                    <Checkbox
                                        checked={value}
                                        name={name}
                                        placeholder={`Email`}
                                        caption={'Email'}
                                        onChange={onChange}
                                    />
                                )}
                            />
                    </div>
                    <div className={styles.Form__WhatsappNotificationsEnabledContainer}>
                        <Controller
                                name="whatsappNotificationsEnabled"
                                control={control}
                                render={({
                                    field: { onChange, onBlur, value, name, ref },
                                    fieldState: { invalid, isTouched, isDirty, error },
                                    formState: { dirtyFields },
                                }) => (
                                    <Checkbox
                                        checked={value}
                                        name={name}
                                        placeholder={`WhatsApp`}
                                        caption={'WhatsApp'}
                                        onChange={onChange}
                                    />
                                )}
                            />
                    </div>
                    <div className={styles.Form__PushNotificationsEnabledContainer}>
                        <PushNotificationInput 
                            onSubscribedChanged={handleSubscribedChanged}
                        />
                    </div>
                    <div className={styles.Form__EmailContainer}>
                        <Controller
                            name="emailForNotifications"
                            control={control}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { invalid, isTouched, isDirty, error },
                                formState: { dirtyFields },
                            }) => (
                                <Input
                                    value={value}
                                    name={name}
                                    placeholder={`Email`}
                                    onValueChange={onChange}
                                    type={'email'}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.Form__PhoneContainer}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { invalid, isTouched, isDirty, error },
                                formState: { dirtyFields },
                            }) => (
                                <Input 
                                    value={value}
                                    name={name}
                                    placeholder={`Phone`}
                                    onValueChange={onChange}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.Form__PushTextContainer}>
                        <div className={styles.Form__PushText}>              
                            <Link 
                                className={styles.Form__PushTextLink}
                                to={`/install/instruction`} 
                                onClick={handleFollowLinkToInstruction}
                            >  For smartphone notification need to install. Learn How
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.Form__EndContainer}>
                <TestNotificationsButton
                    subscribed={subscribed}
                    settings={value}
                />
                <SaveButton
                    onClick={handleSaveButtonClick}
                />
            </div>
        </form>
    )
}